'use client';

import { useEffect, useState } from 'react';
import { motion } from 'motion/react';
import {
  ArrowRight,
  ArrowUpRight,
  Calculator,
  Cpu,
  Gamepad2,
  GraduationCap,
  Layers,
  Sparkles,
  Settings,
  Sun,
  Moon,
  Monitor,
} from 'lucide-react';
import { useI18n } from '@/lib/hooks/use-i18n';
import { LanguageSwitcher } from '@/components/language-switcher';
import { SettingsDialog } from '@/components/settings';
import { InviteLoginButton } from '@/components/invite-login-button';
import { UserModeModal } from '@/components/user-mode-modal';
import { useSettingsStore } from '@/lib/store/settings';
import { useTheme } from '@/lib/hooks/use-theme';
import { cn } from '@/lib/utils';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip';

/**
 * Portal home page.
 *
 * The classroom itself lives at /learn (input a topic → generate a lesson).
 * This page is a stable launch pad: as new applications are added, they are
 * registered in FEATURES below and appear as entries without touching layout.
 */

type Feature = {
  key: string;
  href: string;
  title: string;
  subtitle: string;
  description: string;
  tag: string;
  cta: string;
  image: string;
  /** Tailwind accent classes for icon / tag / hover ring */
  accentText: string;
  accentBg: string;
  accentBorder: string;
  accentShadow: string;
  /**
   * 若设置，卡片会在角标处显示该 API 返回的实时在线状态。
   * 约定响应形如 { online, players, bots, maxClients, map }。
   */
  liveStatusApi?: string;
};

/** 实时游戏服务器状态（/api/xonotic/status 的响应子集） */
type LiveStatus = {
  online: boolean;
  players: number;
  bots: number;
  maxClients: number;
  map?: string;
};

const FEATURES: Feature[] = [
  {
    key: 'pyramid',
    href: '/pyramid/',
    title: '吉萨金字塔群 · 3D 复原',
    subtitle: 'GIZA PLATEAU · 3D ATLAS',
    description:
      '米制真实尺度程序化重建的三大金字塔、狮身人面像、马斯塔巴墓群与高原地形。支持自由旋转、模型分解、建筑聚焦、日间/黄昏光照与线框模式。',
    tag: 'Three.js 3D',
    cta: '进入三维场景',
    image: '/pyramid/card.jpg',
    accentText: 'text-amber-600 dark:text-amber-400',
    accentBg: 'bg-amber-500/15',
    accentBorder: 'border-amber-500/25',
    accentShadow: 'hover:shadow-amber-500/10',
  },
  {
    key: 'racing',
    href: '/racing-game/',
    title: '开罗狂飙 · CAIRO RUSH',
    subtitle: 'WEBGL RACING',
    description:
      '尼罗河滨海道、汗哈利利老城、吉萨金字塔赛道。生涯模式、改装、每日挑战、回放与照片模式，无需下载即点即玩。',
    tag: 'WebGL 3D',
    cta: '开始比赛',
    image: '/racing-game/preview.png',
    accentText: 'text-orange-600 dark:text-orange-400',
    accentBg: 'bg-orange-500/15',
    accentBorder: 'border-orange-500/25',
    accentShadow: 'hover:shadow-orange-500/10',
  },
  {
    key: 'aidc',
    href: '/aidc/',
    title: 'AIDC 智算中心 · 数字孪生',
    subtitle: 'AIDC DIGITAL TWIN',
    description:
      '384 卡 NPU 智算中心的实时数字孪生体：园区微网、24 机柜冷热通道、Spine-Leaf 光互联三层可视化，叠加算力调度、PUE 能耗仿真、热场推演与运维 Agent 策略建议。',
    tag: 'Three.js 3D',
    cta: '进入孪生场景',
    image: '/aidc/card.jpg',
    accentText: 'text-cyan-600 dark:text-cyan-400',
    accentBg: 'bg-cyan-500/15',
    accentBorder: 'border-cyan-500/25',
    accentShadow: 'hover:shadow-cyan-500/10',
  },
  {
    key: 'chronos',
    href: '/chronos/',
    title: 'CHRONOS · 时序之旅',
    subtitle: 'A JOURNEY ALONG THE TIMELINE',
    description:
      '一场一万两千年的自动漫游：从史前草原出发，穿过原始村落与中世纪城堡，驶入暮色中的现代城市。镜头沿时间曲线连续推进，天空、雾、光照与建筑群随章节同步演化。',
    tag: 'Three.js 3D',
    cta: '开始时序之旅',
    image: '/chronos/card.jpg',
    accentText: 'text-amber-700 dark:text-amber-300',
    accentBg: 'bg-amber-500/15',
    accentBorder: 'border-amber-500/25',
    accentShadow: 'hover:shadow-amber-500/10',
  },
  {
    key: 'physlab',
    href: '/physlab/',
    title: 'PhysLab · 物理仿真教学台',
    subtitle: 'INTERACTIVE PHYSICS LAB',
    description:
      '单摆、弹性碰撞、斜面滑块、圆周运动四大实验的实时动力学仿真。拖动滑块即可改写物理参数，配套守恒量遥测、自动自检纠偏与教学 Agent 出题。',
    tag: '物理仿真',
    cta: '开始实验',
    image: '/physlab/card.jpg',
    accentText: 'text-teal-600 dark:text-teal-300',
    accentBg: 'bg-teal-500/15',
    accentBorder: 'border-teal-500/25',
    accentShadow: 'hover:shadow-teal-500/10',
  },
  {
    key: 'xonotic',
    href: '/xonotic/',
    title: 'AI4U Arena · 多人在线竞技射击',
    subtitle: 'AI4U ARENA · POWERED BY XONOTIC',
    description:
      'AI4U Classroom 自建的快节奏竞技场射击场。16 种双模式武器、火箭跳与斜坡滑行，服务器 24 小时常驻，Bot 补位随到随玩，无需下载即可加入战斗。',
    tag: 'AI4U Arena',
    cta: '加入服务器',
    image: '/xonotic/card.jpg',
    accentText: 'text-orange-600 dark:text-orange-400',
    accentBg: 'bg-orange-500/15',
    accentBorder: 'border-orange-500/25',
    accentShadow: 'hover:shadow-orange-500/10',
    liveStatusApi: '/api/xonotic/status',
  },
];

const TOOL_LINKS = [
  { href: '/ai-training/', title: 'AI 训练算力计算器', icon: Calculator },
  { href: '/ai-computing-operation/', title: 'AI 算力运营系统', icon: Cpu },
];

export default function PortalHomePage() {
  const { t } = useI18n();
  const { theme, setTheme } = useTheme();
  const [settingsOpen, setSettingsOpen] = useState(false);

  // ── Live game server status ───────────────────────────────
  // 只有带 liveStatusApi 的卡片才需要轮询。当前只有 Xonotic 一台服务器，
  // 所以用一个 state 保存即可；将来多台游戏时改成 Record<key, LiveStatus>。
  const [liveStatus, setLiveStatus] = useState<LiveStatus | null>(null);

  useEffect(() => {
    const feature = FEATURES.find((f) => f.liveStatusApi);
    if (!feature?.liveStatusApi) return;

    let cancelled = false;

    const load = () => {
      fetch(feature.liveStatusApi!, { cache: 'no-store' })
        .then((r) => (r.ok ? r.json() : null))
        .then((data) => {
          if (cancelled || !data) return;
          setLiveStatus({
            online: !!data.online,
            players: Number(data.players ?? 0),
            bots: Number(data.bots ?? 0),
            maxClients: Number(data.maxClients ?? 0),
            map: data.map,
          });
        })
        .catch(() => {
          if (!cancelled) {
            setLiveStatus({ online: false, players: 0, bots: 0, maxClients: 0 });
          }
        });
    };

    load();
    // 60s 与服务端缓存 TTL 对齐，两次请求之间基本命中缓存
    const timer = setInterval(load, 60_000);

    // 标签页切回前台时立刻刷新一次，避免显示长时间未更新的数字
    const onVisible = () => {
      if (document.visibilityState === 'visible') load();
    };
    document.addEventListener('visibilitychange', onVisible);

    return () => {
      cancelled = true;
      clearInterval(timer);
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, []);

  // ── Classroom entry gate ──────────────────────────────────
  // The portal is open to everyone. Identity is only needed once the visitor
  // actually enters the classroom, so we ask here and then continue to /learn.
  const [modeModalOpen, setModeModalOpen] = useState(false);
  const [modeGateReady, setModeGateReady] = useState(false);
  const [modeGate, setModeGate] = useState<{ enabled: boolean; invited: boolean }>({
    enabled: false,
    invited: false,
  });
  const fetchServerProviders = useSettingsStore((s) => s.fetchServerProviders);

  useEffect(() => {
    let cancelled = false;
    Promise.all([
      fetch('/api/access-code/status').then((r) => r.json()).catch(() => ({ enabled: false })),
      fetch('/api/invite/status').then((r) => r.json()).catch(() => ({ enabled: false, invited: false })),
    ])
      .then(([access, invite]) => {
        if (cancelled) return;
        // Legacy access-code gate takes precedence and is handled by the guard.
        setModeGate({
          enabled: !!invite.enabled && !access.enabled,
          invited: !!invite.invited,
        });
        setModeGateReady(true);
      })
      .catch(() => {
        if (!cancelled) setModeGateReady(true);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  /** Proceed into the classroom once the visitor has an identity. */
  const enterClassroom = () => {
    let alreadyChosen = false;
    try {
      alreadyChosen =
        localStorage.getItem('openmaic_guest_mode') === 'true' || modeGate.invited;
    } catch {
      /* ignore */
    }
    if (!modeGateReady || !modeGate.enabled || alreadyChosen) {
      window.location.href = '/learn';
      return;
    }
    setModeModalOpen(true);
  };

  const handleGuestEnter = () => {
    try {
      localStorage.setItem('openmaic_guest_mode', 'true');
    } catch {
      /* ignore */
    }
    setModeModalOpen(false);
    window.location.href = '/learn';
  };

  const handleInviteSuccess = (name: string) => {
    void name;
    fetchServerProviders();
    setModeModalOpen(false);
    window.location.href = '/learn';
  };

  // Prefetch heavy app entries so the click feels instant
  useEffect(() => {
    const timer = window.setTimeout(() => {
      FEATURES.forEach((f) => {
        const link = document.createElement('link');
        link.rel = 'prefetch';
        link.href = f.href;
        document.head.appendChild(link);
      });
    }, 1200);
    return () => window.clearTimeout(timer);
  }, []);

  return (
    <div className="min-h-[100dvh] w-full flex flex-col bg-background">
      {/* ═══ Navigation Bar ═══ */}
      <nav className="sticky top-0 z-50 w-full border-b border-border/60 bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/80">
        <div className="mx-auto max-w-6xl flex h-14 items-center justify-between gap-2 px-4 md:px-6">
          <a href="/" className="flex items-center gap-2 group shrink-0">
            <svg viewBox="0 0 40 40" className="h-8 w-8 md:h-9 md:w-9 shrink-0" aria-hidden="true">
              <defs>
                <linearGradient id="ig" x1="0%" y1="0%" x2="100%" y2="100%">
                  <stop offset="0%" stopColor="#3b82f6" />
                  <stop offset="100%" stopColor="#60a5fa" />
                </linearGradient>
                <linearGradient id="ag" x1="0%" y1="0%" x2="100%" y2="100%">
                  <stop offset="0%" stopColor="#f59e0b" />
                  <stop offset="100%" stopColor="#fbbf24" />
                </linearGradient>
              </defs>
              <path d="M8 28 Q8 10 20 10 L20 32 Q8 32 8 28Z" fill="url(#ig)" opacity="0.9" />
              <path d="M20 10 Q32 10 32 28 Q32 32 20 32Z" fill="url(#ag)" opacity="0.9" />
            </svg>
            <span className="text-sm md:text-base font-bold tracking-tight text-foreground">
              AI4U Classroom
            </span>
          </a>

          <div className="flex items-center gap-1 md:gap-2">
            <LanguageSwitcher />
            <Tooltip>
              <TooltipTrigger asChild>
                <button
                  onClick={() => setTheme(theme === 'dark' ? 'light' : 'dark')}
                  className="p-2 rounded-lg text-muted-foreground hover:bg-accent hover:text-foreground transition-colors"
                >
                  {theme === 'dark' ? <Sun className="w-4 h-4" /> : <Moon className="w-4 h-4" />}
                </button>
              </TooltipTrigger>
              <TooltipContent>切换主题</TooltipContent>
            </Tooltip>
            <InviteLoginButton />
            <div className="w-[1px] h-4 bg-border/60" />
            {TOOL_LINKS.map(({ href, title, icon: Icon }) => (
              <Tooltip key={href}>
                <TooltipTrigger asChild>
                  <a
                    href={href}
                    className="p-2 rounded-lg text-muted-foreground hover:bg-accent hover:text-foreground transition-colors"
                  >
                    <Icon className="w-4 h-4" />
                  </a>
                </TooltipTrigger>
                <TooltipContent>{title}</TooltipContent>
              </Tooltip>
            ))}
            <div className="w-[1px] h-4 bg-border/60" />
            <button
              onClick={() => setSettingsOpen(true)}
              className="p-2 rounded-lg text-muted-foreground hover:bg-accent hover:text-foreground transition-colors"
            >
              <Settings className="w-4 h-4" />
            </button>
          </div>
        </div>
      </nav>

      <SettingsDialog
        open={settingsOpen}
        onOpenChange={(open) => setSettingsOpen(open)}
      />

      <UserModeModal
        open={modeModalOpen}
        inviteEnabled={modeGate.enabled}
        onGuestEnter={handleGuestEnter}
        onInviteSuccess={handleInviteSuccess}
      />

      {/* ═══ Main Content ═══ */}
      <main className="flex-1 w-full">
        <div className="mx-auto max-w-6xl px-4 md:px-6 pt-12 md:pt-16 pb-16">
          {/* ── Hero ── */}
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, ease: 'easeOut' }}
            className="text-center max-w-3xl mx-auto"
          >
            <div className="inline-flex items-center gap-1.5 rounded-full border border-border bg-card px-3 py-1 text-[11px] font-medium text-muted-foreground mb-5">
              <Sparkles className="size-3 text-amber-500" />
              AI4U Classroom · 生成式学习平台
            </div>
            <h1 className="text-2xl md:text-4xl font-bold text-foreground tracking-tight mb-3">
              {t('home.slogan')}
            </h1>
            <p className="text-sm md:text-base text-muted-foreground">
              Generative Learning in Multi-Agent Interactive Classroom
            </p>
          </motion.div>

          {/* ── Primary entry: AI Classroom ── */}
          <motion.div
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1, duration: 0.45, ease: 'easeOut' }}
            className="mt-8 md:mt-10"
          >
            <button
              type="button"
              onClick={enterClassroom}
              className="group relative block w-full text-left overflow-hidden rounded-2xl border border-primary/25 bg-gradient-to-br from-primary/10 via-card to-card p-5 md:p-7 hover:border-primary/50 transition-all hover:shadow-xl hover:shadow-primary/10"
            >
              <div className="flex items-start gap-4 md:gap-5">
                <div className="flex size-11 md:size-14 shrink-0 items-center justify-center rounded-xl bg-primary/15 border border-primary/25">
                  <GraduationCap className="size-6 md:size-7 text-primary" />
                </div>
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2 flex-wrap">
                    <h2 className="text-base md:text-xl font-bold text-foreground">AI 教室</h2>
                    <span className="shrink-0 rounded-full bg-primary/15 px-2 py-0.5 text-[10px] font-medium text-primary border border-primary/25">
                      核心功能
                    </span>
                  </div>
                  <p className="mt-1.5 text-xs md:text-sm text-muted-foreground leading-relaxed">
                    输入任意想学的主题，AI 多智能体自动生成互动课堂：幻灯片、讲稿、白板演示与实时答疑。支持导入已有课堂继续学习。
                  </p>
                  <div className="mt-3 flex flex-wrap items-center gap-x-4 gap-y-1 text-[11px] text-muted-foreground">
                    <span className="inline-flex items-center gap-1"><Layers className="size-3" /> 多智能体生成</span>
                    <span className="inline-flex items-center gap-1"><Monitor className="size-3" /> 白板 + 幻灯片</span>
                    <span className="inline-flex items-center gap-1"><Sparkles className="size-3" /> 实时互动答疑</span>
                  </div>
                </div>
                <div className="hidden sm:flex shrink-0 items-center gap-1 self-center rounded-lg bg-primary px-3.5 py-2 text-xs font-medium text-primary-foreground group-hover:gap-2 transition-all">
                  进入教室
                  <ArrowRight className="size-3.5 transition-transform group-hover:translate-x-0.5" />
                </div>
              </div>
              <div className="mt-3 sm:hidden flex items-center gap-1 text-xs font-medium text-primary">
                进入教室
                <ArrowRight className="size-3.5" />
              </div>
            </button>
          </motion.div>

          {/* ── Feature apps ── */}
          <motion.div
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2, duration: 0.45, ease: 'easeOut' }}
            className="mt-10 md:mt-12"
          >
            <div className="mb-4 flex items-baseline justify-between gap-3">
              <h2 className="flex items-center gap-2 text-sm font-semibold text-foreground">
                <Gamepad2 className="size-4 text-muted-foreground" />
                功能应用
              </h2>
              <span className="text-[11px] text-muted-foreground">3D 可视化 · 互动实验 · 持续新增</span>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-5">
              {FEATURES.map((f, i) => (
                <motion.a
                  key={f.key}
                  href={f.href}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.25 + i * 0.07, duration: 0.4, ease: 'easeOut' }}
                  className={cn(
                    'group relative flex flex-col overflow-hidden rounded-xl border border-border bg-card transition-all hover:shadow-lg',
                    'hover:border-foreground/20',
                    f.accentShadow,
                    // With an odd number of cards on a 3-col grid, the trailing card
                    // spans two columns so the last row closes cleanly.
                    i === FEATURES.length - 1 && FEATURES.length % 3 !== 0
                      ? 'lg:col-span-2'
                      : '',
                  )}
                >
                  {(() => {
                    // The trailing card on an odd count spans two columns; lay it out
                    // side-by-side (image | copy) so its height matches its neighbours
                    // instead of stretching a full-width 16:9 image.
                    const isWide = i === FEATURES.length - 1 && FEATURES.length % 3 !== 0;
                    const media = (
                      <div
                        className={cn(
                          'relative overflow-hidden bg-black',
                          isWide
                            ? 'aspect-[16/9] w-full lg:aspect-auto lg:h-full lg:w-[52%] lg:shrink-0'
                            : 'aspect-[16/9] w-full',
                        )}
                      >
                        {/* eslint-disable-next-line @next/next/no-img-element */}
                        <img
                          src={f.image}
                          alt={f.title}
                          loading="lazy"
                          className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
                        />
                        <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/10 to-transparent" />
                        <div className="absolute left-3 top-3">
                          <span
                            className={cn(
                              'rounded-full px-2.5 py-1 text-[10px] font-medium border backdrop-blur-sm',
                              f.accentBg,
                              f.accentText,
                              f.accentBorder,
                            )}
                          >
                            {f.tag}
                          </span>
                        </div>
                        {/* ── 实时在线状态（仅 liveStatusApi 卡片）──────────
                            在线显示绿点 + 人数 + 地图；离线显示灰点「服务器离线」。
                            未取到数据（首屏加载中）时不渲染，避免闪烁出「离线」。 */}
                        {f.liveStatusApi && liveStatus && (
                          <div className="absolute right-3 top-3">
                            <span
                              className={cn(
                                'inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-[10px] font-semibold shadow-sm',
                                liveStatus.online
                                  ? 'border-emerald-400/50 bg-emerald-950/75 text-emerald-100 backdrop-blur-md'
                                  : 'border-white/25 bg-black/70 text-white/70 backdrop-blur-md',
                              )}
                              title={
                                liveStatus.online
                                  ? `在线 · 当前地图 ${liveStatus.map ?? '未知'} · ${liveStatus.bots} 个 Bot 陪玩`
                                  : '服务器当前离线'
                              }
                            >
                              <span
                                className={cn(
                                  'relative flex size-1.5',
                                  liveStatus.online ? '' : 'opacity-60',
                                )}
                              >
                                {liveStatus.online && (
                                  <span className="absolute inline-flex size-full animate-ping rounded-full bg-emerald-400 opacity-75" />
                                )}
                                <span
                                  className={cn(
                                    'relative inline-flex size-1.5 rounded-full',
                                    liveStatus.online ? 'bg-emerald-400' : 'bg-white/50',
                                  )}
                                />
                              </span>
                              {liveStatus.online ? (
                                <>
                                  <span className="tabular-nums">
                                    {liveStatus.players}/{liveStatus.maxClients}
                                  </span>
                                  <span className="text-emerald-200/60">·</span>
                                  <span className="max-w-[68px] truncate capitalize">
                                    {liveStatus.map ?? '在线'}
                                  </span>
                                </>
                              ) : (
                                <span>服务器离线</span>
                              )}
                            </span>
                          </div>
                        )}
                        <div className="absolute bottom-3 left-3 right-3 flex items-end justify-between gap-2">
                          <div className="min-w-0">
                            <div className="text-[11px] font-medium tracking-wide text-white/65">{f.subtitle}</div>
                            <h3 className="truncate text-base md:text-lg font-semibold text-white">{f.title}</h3>
                          </div>
                          <ArrowUpRight className="size-4 shrink-0 text-white/70 transition-transform group-hover:-translate-y-0.5 group-hover:translate-x-0.5" />
                        </div>
                      </div>
                    );
                    const copy = (
                      <div
                        className={cn(
                          'flex flex-1 flex-col p-4 md:p-5',
                          isWide && 'lg:justify-center',
                        )}
                      >
                        <p className="text-[13px] text-muted-foreground leading-relaxed line-clamp-4">
                          {f.description}
                        </p>
                        {/* 在线状态在文字区再出现一次：图片上的徽章在小屏/快速滚动时
                            容易被忽略，这里补一条可读的纯文本提示。 */}
                        {f.liveStatusApi && liveStatus?.online && (
                          <div className="mt-2 flex items-center gap-1.5 text-[11px] text-muted-foreground">
                            <span className="relative flex size-1.5">
                              <span className="absolute inline-flex size-full animate-ping rounded-full bg-emerald-500 opacity-60" />
                              <span className="relative inline-flex size-1.5 rounded-full bg-emerald-500" />
                            </span>
                            <span>
                              服务器在线
                              <span className="mx-1 text-border">|</span>
                              <span className="tabular-nums text-foreground/80">
                                {liveStatus.players}/{liveStatus.maxClients}
                              </span>{' '}
                              位玩家
                              {liveStatus.bots > 0 && (
                                <>
                                  <span className="mx-1 text-border">|</span>
                                  {liveStatus.bots} 个 Bot 陪玩
                                </>
                              )}
                            </span>
                          </div>
                        )}
                        <span
                          className={cn(
                            'mt-3 inline-flex items-center gap-1 text-[13px] font-medium',
                            f.accentText,
                          )}
                        >
                          {f.cta}
                          <ArrowRight className="size-3" />
                        </span>
                      </div>
                    );
                    return isWide ? (
                      <div className="flex flex-1 flex-col lg:flex-row">{media}{copy}</div>
                    ) : (
                      <>{media}{copy}</>
                    );
                  })()}
                </motion.a>
              ))}
            </div>
          </motion.div>
        </div>
      </main>

      {/* ═══ Footer ═══ */}
      <footer className="w-full border-t border-border/60 py-6">
        <div className="mx-auto max-w-6xl px-4 md:px-6 flex flex-col sm:flex-row items-center justify-between gap-2 text-[11px] text-muted-foreground">
          <span>AI4U Classroom</span>
          <span>Built on open-source projects for learning and communication, not for commercial use</span>
          <span>蜀ICP备2026018687</span>
        </div>
      </footer>
    </div>
  );
}
